import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

// Components
import ReadMore from '../../_molecules/ReadMore';
import LinkedInIcon from '@icons/LinkedInIcon';
import TwitterIcon from '@icons/TwitterIcon';
import AddressBlock from '@molecules/AddressBlock';
import { DivWithShadowAlternate, LabelLarge, LinkSmall } from '@styled';

// Functionality
import { mediaQueries } from '@general/breakpoints';
import fonts from '@general/fonts';
import useCompanyFull from '@hooks/useCompanyFull';

const ContainerStyled = styled(DivWithShadowAlternate)`
    height: 100%;
    padding: 60px;
`;

const Row = styled.div`
    display: flex;
    flex-wrap: nowrap;

    span,
    a {
        line-height: 18px;
    }
`;

const Label = styled(LabelLarge)`
    width: 5.5rem;
    flex: 0 0 5.5rem;
`;

const BodySmall = styled.div`
    font-family: ${fonts.sans};
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 21px;
    margin: 0;
`;

const ContainerInnerStyled = styled.div`
    display: flex;
    flex-direction: column;
    grid-template-columns: 1fr;
    grid-template-rows: repeat(2, 1fr);
    grid-column-gap: 0;
    grid-row-gap: 30px;
    grid-column-gap: 1rem;

    @media ${mediaQueries.md} {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: 1fr;
    }
    @media ${mediaQueries.lg} {
        display: flex;
        flex-direction: column;
    }
    @media ${mediaQueries.xl} {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: 1fr;
    }
`;

const CompanyProfileDetailsCard = ({ graphID }) => {
    const { data } = useCompanyFull(graphID);
    const address = data?.addresses[0];

    if (!data) return null;

    return (
        <ContainerStyled>
            <ContainerInnerStyled>
                <div>
                    {data.website && (
                        <Row className="mb-1.25rem">
                            <Label>Website:</Label>
                            <LinkSmall href={data.website} target="_blank">
                                {data.website}
                            </LinkSmall>
                        </Row>
                    )}
                    {address && (
                        <Row className="mb-1.25rem">
                            <Label>Address:</Label>
                            <BodySmall>
                                <AddressBlock address={address} />
                            </BodySmall>
                        </Row>
                    )}
                    {(data.linkedinUrl || data.twitterUrl) && (
                        <Row className="mb-1rem">
                            <Label>Social:</Label>
                            <div className="d-flex">
                                {data.linkedinUrl && (
                                    <a className="mr-1.5rem" href={data.linkedinUrl} target="_blank" rel="noreferrer">
                                        <LinkedInIcon />
                                    </a>
                                )}

                                {data.twitterUrl && (
                                    <a className="mr-1.5rem" href={data.twitterUrl} target="_blank" rel="noreferrer">
                                        <TwitterIcon />
                                    </a>
                                )}
                            </div>
                        </Row>
                    )}
                </div>
                <div>
                    {data.description && (
                        <Row className="mb-1.25rem">
                            <Label>About:</Label>
                            <BodySmall>
                                <ReadMore>{data.description}</ReadMore>
                            </BodySmall>
                        </Row>
                    )}
                </div>
            </ContainerInnerStyled>
        </ContainerStyled>
    );
};

CompanyProfileDetailsCard.propTypes = {
    graphID: PropTypes.string,
};

export default CompanyProfileDetailsCard;
